import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DespachoInfoCallout,
  DespachoMockBanner,
  DespachoPageShell,
  DespachoSectionCard,
} from "@/components/despacho/DespachoUi";
import ClientesAtencionPanel from "@/components/despacho/ClientesAtencionPanel";
import {
  SOPORTE_BNT_CATEGORIAS,
  mockSoporteBnt,
} from "@/modules/despacho/despachoMockData";

const MAIN_TABS = [
  { id: "clientes", label: "Atención a clientes" },
  { id: "bnt", label: "Soporte BNT" },
];

/**
 * Bandeja operativa de atención a clientes (WhatsApp Business compartido) — requiere Gestión Despacho.
 * El chat interno general vive en /equipo/chat (fuera de este módulo).
 */
export default function CentroMensajesDespacho() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "clientes";
  const [canal, setCanal] = useState(
    MAIN_TABS.some((t) => t.id === tabParam) ? tabParam : "clientes",
  );
  const [categoriaBnt, setCategoriaBnt] = useState("Todas");

  useEffect(() => {
    if (MAIN_TABS.some((t) => t.id === tabParam)) {
      setCanal(tabParam);
    } else if (tabParam === "equipo") {
      setCanal("clientes");
    }
  }, [tabParam]);

  const setCanalTab = (id) => {
    setCanal(id);
    setSearchParams({ tab: id }, { replace: true });
  };

  return (
    <DespachoPageShell
      title="Atención a clientes"
      subtitle="Bandeja compartida tipo WhatsApp Business del despacho. Requiere licencia Gestión Despacho."
      testId="despacho-atencion-clientes"
    >
      <DespachoInfoCallout>
        <strong>Operación avanzada:</strong> conversaciones por cliente, responsable, prioridad y reasignación.
        El <strong>chat interno del equipo</strong> está en el Panel General →{" "}
        <strong>Chat interno</strong> (<code className="text-[10px]">/equipo/chat</code>) y no requiere este módulo.
      </DespachoInfoCallout>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Canales de atención">
        {MAIN_TABS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant={canal === tab.id ? "default" : "outline"}
            className={canal === tab.id ? "rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" : "rounded-sm"}
            onClick={() => setCanalTab(tab.id)}
            data-testid={`despacho-msgs-tab-${tab.id}`}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {canal === "clientes" ? (
        <div className="space-y-4" data-testid="despacho-msgs-clientes">
          <ClientesAtencionPanel viewerRole="owner" />
        </div>
      ) : null}

      {canal === "bnt" ? (
        <div className="space-y-4" data-testid="despacho-msgs-bnt">
          <DespachoInfoCallout>
            Comunicación entre el <strong>comprador/dueño del despacho</strong> y BNT (soporte, licencias,
            promociones, avisos). No visible para auxiliares por defecto.
          </DespachoInfoCallout>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={categoriaBnt === "Todas" ? "secondary" : "ghost"}
              className="rounded-sm"
              onClick={() => setCategoriaBnt("Todas")}
            >
              Todas
            </Button>
            {SOPORTE_BNT_CATEGORIAS.map((cat) => (
              <Button
                key={cat}
                type="button"
                size="sm"
                variant={categoriaBnt === cat ? "secondary" : "ghost"}
                className="rounded-sm"
                onClick={() => setCategoriaBnt(cat)}
              >
                {cat}
              </Button>
            ))}
          </div>

          <DespachoSectionCard title="Bandeja Soporte BNT">
            <ul className="space-y-2 m-0 p-0 list-none">
              {mockSoporteBnt
                .filter((row) => categoriaBnt === "Todas" || row.categoria === categoriaBnt)
                .map((row) => (
                  <li
                    key={row.asunto}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <Badge variant="outline" className="text-[10px] mr-2 font-normal">
                        {row.categoria}
                      </Badge>
                      <span className="font-medium text-[#0F2D5C]">{row.asunto}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {row.estado} · {row.fecha}
                    </span>
                  </li>
                ))}
            </ul>
          </DespachoSectionCard>
        </div>
      ) : null}

      <DespachoMockBanner compact />
    </DespachoPageShell>
  );
}
